import React, { useState } from 'react';
import { Link, Outlet, useNavigate } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import Swal from 'sweetalert2';
import { useAuthStore } from '../stores/authStore';
import { useEnvironment } from '../contexts/EnvironmentContext';
import { useTheme } from '../contexts/ThemeContext';
import { api } from '../lib/api';
import type { Subscription } from '../types';
import { MessageSquare, LogOut, Beaker, Rocket, Sun, Moon, Users } from 'lucide-react';

export const Layout: React.FC = () => {
  const { user, client, isAuthenticated, clearAuth } = useAuthStore();
  const { environment, setEnvironment } = useEnvironment();
  const { theme, toggleTheme } = useTheme();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [profileMenuOpen, setProfileMenuOpen] = useState(false);

  const { data: subscriptions } = useQuery({
    queryKey: ['subscriptions', environment, client?.id],
    queryFn: async () => {
      const response = await api.get<{ success: boolean; data: Subscription[] }>(
        `/clients/${client?.id}/subscriptions`
      );
      return response.data.data.filter((sub) => {
        const app = (sub as any).application;
        if (app && app.sandbox_mode !== undefined && app.sandbox_mode !== null) {
          return app.sandbox_mode === (environment === 'sandbox');
        }
        return environment === 'sandbox';
      });
    },
    enabled: !!client?.id && isAuthenticated,
  });

  const activeSubscriptionsCount = subscriptions?.filter((s) => s.status === 'active').length || 0;
  // This variable is no longer displayed but kept for future analytics/badges
  console.debug('Active subscriptions:', activeSubscriptionsCount);

  const handleLogout = () => {
    clearAuth();
    navigate('/login');
  };

  // Switching to production requires explicit confirmation
  const handleEnvironmentChange = async (env: 'sandbox' | 'production') => {
    if (env === environment) return;

    if (env === 'production') {
      const result = await Swal.fire({
        title: 'Mudar para Produção?',
        html: `
          <div style="text-align:left;margin-top:1rem">
            <p style="margin-bottom:1rem">Está prestes a mudar para o ambiente de <strong>Produção</strong>.</p>
            <div style="background:#FEF3C7;border:1px solid #FCD34D;border-radius:8px;padding:1rem;margin-bottom:1rem">
              <p style="color:#92400E;font-weight:600;margin-bottom:0.5rem">⚠️ Atenção:</p>
              <ul style="color:#92400E;margin:0;padding-left:1.5rem">
                <li>As chamadas à API serão <strong>reais e cobradas</strong></li>
                <li>Verá aplicações e subscrições de <strong>produção</strong></li>
                <li>As credenciais são diferentes das de sandbox</li>
              </ul>
            </div>
          </div>
        `,
        icon: 'warning',
        showCancelButton: true,
        confirmButtonColor: '#059669',
        cancelButtonColor: '#6B7280',
        confirmButtonText: 'Sim, mudar para Produção',
        cancelButtonText: 'Cancelar',
        width: '520px',
        background: theme === 'dark' ? '#0f172a' : '#ffffff',
        color: theme === 'dark' ? '#f8fafc' : '#0f172a',
        customClass: {
          popup:
            'bg-white dark:bg-slate-900 rounded-2xl shadow-xl border border-slate-200 dark:border-slate-800',
        },
      });
      if (!result.isConfirmed) return;
    }

    setEnvironment(env);
  };

  return (
    <div className="min-h-screen flex flex-col bg-slate-50 dark:bg-slate-950 transition-colors duration-300">
      {/* Header */}
      <header className="bg-glass sticky top-0 z-50 shadow-sm border-b border-transparent dark:border-slate-800/50">
        <div className="container-custom">
          <div className="flex justify-between items-center h-16">
            {/* Logo */}
            <div className="flex items-center">
              <Link
                to="/"
                className="flex items-center gap-2 font-bold text-xl text-secondary-600 hover:text-secondary-700 transition-colors"
              >
                <svg
                  width="32"
                  height="32"
                  viewBox="0 0 32 32"
                  fill="none"
                  xmlns="http://www.w3.org/2000/svg"
                >
                  <rect width="32" height="32" rx="8" className="fill-secondary-600" />
                  <path d="M8 16L16 8L24 16L16 24L8 16Z" fill="white" />
                </svg>
                API Marketplace
              </Link>
            </div>

            {/* Desktop Navigation */}
            {!isAuthenticated ? (
              <nav className="hidden lg:flex items-center gap-1">
                <a
                  href="/#catalogo-publico"
                  className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white transition-colors text-sm font-semibold"
                >
                  Catálogo de APIs
                </a>
                <Link
                  to="/status"
                  className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white transition-colors text-sm font-semibold flex items-center gap-1.5"
                >
                  <span className="w-2 h-2 rounded-full bg-emerald-500 animate-pulse" />
                  Status do Gateway
                </Link>
                <Link
                  to="/login"
                  className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white transition-colors text-sm font-semibold"
                >
                  Documentação
                </Link>
              </nav>
            ) : (
              <nav className="hidden lg:flex items-center gap-1">
                <Link
                  to="/catalog"
                  className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white transition-colors text-sm font-semibold"
                >
                  Catálogo
                </Link>
                <Link
                  to="/applications"
                  className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white transition-colors text-sm font-semibold"
                >
                  Aplicações
                </Link>
                <Link
                  to="/wallet"
                  className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white transition-colors text-sm font-semibold"
                >
                  Carteira
                </Link>
                <Link
                  to="/analytics"
                  className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white transition-colors text-sm font-semibold"
                >
                  Analytics
                </Link>
                <Link
                  to="/team"
                  className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white transition-colors text-sm font-semibold"
                >
                  Equipa
                </Link>
                <Link
                  to="/status"
                  className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 hover:text-slate-900 dark:text-slate-200 dark:hover:bg-slate-800 dark:hover:text-white transition-colors text-sm font-semibold flex items-center gap-1.5"
                >
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Status
                </Link>
              </nav>
            )}

            {/* Right Side */}
            <div className="flex items-center gap-3">
              {/* Theme Toggle (Always visible) */}
              <button
                onClick={toggleTheme}
                className="p-2 rounded-lg text-slate-600 hover:bg-slate-100 dark:text-slate-300 dark:hover:bg-slate-800 transition-colors"
                title={theme === 'dark' ? 'Mudar para modo claro' : 'Mudar para modo escuro'}
              >
                {theme === 'dark' ? <Sun className="w-5 h-5" /> : <Moon className="w-5 h-5" />}
              </button>

              {!isAuthenticated ? (
                <div className="flex items-center gap-2">
                  <Link
                    to="/login"
                    className="px-3.5 py-1.5 rounded-xl text-xs sm:text-sm font-bold text-slate-700 dark:text-slate-200 hover:bg-slate-100 dark:hover:bg-slate-800 transition-colors"
                  >
                    Iniciar Sessão
                  </Link>
                  <Link
                    to="/register"
                    className="px-4 py-1.5 rounded-xl text-xs sm:text-sm font-bold bg-primary-600 hover:bg-primary-700 text-white shadow-xs hover:shadow transition-all"
                  >
                    Registar
                  </Link>
                  {/* Mobile Menu Button for Unauthenticated */}
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="lg:hidden p-2 rounded-lg text-slate-600 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {mobileMenuOpen ? (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      ) : (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 6h16M4 12h16M4 18h16"
                        />
                      )}
                    </svg>
                  </button>
                </div>
              ) : (
                <>
                  {/* Environment Switcher */}
                  <div className="hidden md:flex items-center rounded-xl overflow-hidden border border-slate-200 dark:border-slate-700 text-xs">
                    <button
                      onClick={() => handleEnvironmentChange('sandbox')}
                      className={`px-3 py-1.5 font-semibold transition-all flex items-center gap-1.5 ${
                        environment === 'sandbox'
                          ? 'bg-amber-500 text-white'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      <Beaker className="w-4 h-4" /> Sandbox
                    </button>
                    <button
                      onClick={() => handleEnvironmentChange('production')}
                      className={`px-3 py-1.5 font-semibold transition-all flex items-center gap-1.5 ${
                        environment === 'production'
                          ? 'bg-emerald-600 text-white'
                          : 'bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 hover:bg-slate-200 dark:hover:bg-slate-700'
                      }`}
                    >
                      <Rocket className="w-4 h-4" /> Produção
                    </button>
                  </div>

                  {/* Profile Dropdown */}
                  <div className="relative hidden md:block">
                    <button
                      onClick={() => setProfileMenuOpen(!profileMenuOpen)}
                      className="flex items-center gap-2 px-3 py-2 rounded-lg hover:bg-gray-100 dark:hover:bg-slate-800 transition-colors"
                    >
                      <div className="text-right">
                        <div
                          className="text-gray-900 dark:text-white"
                          style={{ fontSize: 'var(--text-sm)', fontWeight: 'var(--font-medium)' }}
                        >
                          {client?.company_name}
                        </div>
                        <div
                          className="text-gray-500 dark:text-gray-400"
                          style={{ fontSize: 'var(--text-xs)' }}
                        >
                          {user?.email}
                        </div>
                      </div>
                      <svg
                        className={`w-4 h-4 text-gray-500 transition-transform ${profileMenuOpen ? 'rotate-180' : ''}`}
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M19 9l-7 7-7-7"
                        />
                      </svg>
                    </button>

                    {/* Dropdown Menu */}
                    {profileMenuOpen && (
                      <div
                        className="absolute right-0 mt-2 w-56 bg-white dark:bg-slate-900 rounded-lg shadow-lg border border-slate-200 dark:border-slate-700 py-1"
                        style={{ zIndex: 'var(--z-dropdown)' }}
                      >
                        <Link
                          to="/team"
                          className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center gap-2"
                          onClick={() => setProfileMenuOpen(false)}
                        >
                          <Users className="w-4 h-4 text-primary-500" /> Equipa
                        </Link>
                        <Link
                          to="/support"
                          className="px-4 py-2 text-sm text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-slate-800 flex items-center gap-2"
                          onClick={() => setProfileMenuOpen(false)}
                        >
                          <MessageSquare className="w-4 h-4" /> Suporte
                        </Link>
                        <div className="border-t border-gray-200 dark:border-slate-700 my-1"></div>
                        <button
                          onClick={() => {
                            setProfileMenuOpen(false);
                            handleLogout();
                          }}
                          className="w-full text-left px-4 py-2 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-900/20 flex items-center gap-2"
                        >
                          <LogOut className="w-4 h-4" /> Sair
                        </button>
                      </div>
                    )}
                  </div>

                  {/* Mobile Menu Button */}
                  <button
                    onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                    className="lg:hidden p-2 rounded-lg hover:bg-gray-100"
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      {mobileMenuOpen ? (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      ) : (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 6h16M4 12h16M4 18h16"
                        />
                      )}
                    </svg>
                  </button>
                </>
              )}
            </div>
          </div>

          {/* Mobile Menu */}
          {mobileMenuOpen && (
            <div className="lg:hidden border-t border-gray-200 dark:border-slate-700 py-4">
              {isAuthenticated ? (
                <nav className="flex flex-col gap-2">
                  <Link
                    to="/catalog"
                    className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Catálogo
                  </Link>
                  <Link
                    to="/applications"
                    className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Aplicações
                  </Link>
                  <Link
                    to="/wallet"
                    className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Carteira
                  </Link>
                  <Link
                    to="/analytics"
                    className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Analytics
                  </Link>
                  <Link
                    to="/status"
                    className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800 flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="w-2 h-2 rounded-full bg-emerald-500" /> Status do Gateway
                  </Link>
                  <Link
                    to="/team"
                    className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Equipa
                  </Link>
                  <Link
                    to="/support"
                    className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Suporte
                  </Link>

                  {/* Environment Switcher Mobile */}
                  <div className="flex items-center gap-2 px-3 py-2">
                    <span className="text-gray-600 text-sm font-medium">Ambiente:</span>
                    <button
                      onClick={() => {
                        handleEnvironmentChange('sandbox');
                        setMobileMenuOpen(false);
                      }}
                      className="flex-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5"
                      style={{
                        backgroundColor:
                          environment === 'sandbox'
                            ? 'var(--color-warning-500)'
                            : 'var(--color-gray-100)',
                        color: environment === 'sandbox' ? 'white' : 'var(--color-gray-700)',
                      }}
                    >
                      <Beaker className="w-4 h-4" /> Sandbox
                    </button>
                    <button
                      onClick={() => {
                        handleEnvironmentChange('production');
                        setMobileMenuOpen(false);
                      }}
                      className="flex-1 px-3 py-1.5 rounded-lg text-sm font-medium transition-all flex items-center justify-center gap-1.5"
                      style={{
                        backgroundColor:
                          environment === 'production'
                            ? 'var(--color-success-500)'
                            : 'var(--color-gray-100)',
                        color: environment === 'production' ? 'white' : 'var(--color-gray-700)',
                      }}
                    >
                      <Rocket className="w-4 h-4" /> Produção
                    </button>
                  </div>

                  <button
                    onClick={handleLogout}
                    className="mx-3 mt-2 btn-base btn-secondary btn-sm"
                  >
                    Sair
                  </button>
                </nav>
              ) : (
                <nav className="flex flex-col gap-2">
                  <a
                    href="/#catalogo-publico"
                    className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Catálogo de APIs
                  </a>
                  <Link
                    to="/status"
                    className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800 flex items-center gap-2"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    <span className="w-2 h-2 rounded-full bg-emerald-500" /> Status do Gateway
                  </Link>
                  <Link
                    to="/login"
                    className="px-3 py-2 rounded-lg text-slate-700 hover:bg-slate-100 dark:text-slate-200 dark:hover:bg-slate-800"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Iniciar Sessão
                  </Link>
                  <Link
                    to="/register"
                    className="mx-3 mt-2 px-4 py-2 rounded-xl text-center text-sm font-bold bg-primary-600 hover:bg-primary-700 text-white shadow-xs"
                    onClick={() => setMobileMenuOpen(false)}
                  >
                    Registar Conta Gratuita
                  </Link>
                </nav>
              )}
            </div>
          )}
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1">
        <Outlet />
      </main>

      {/* Footer */}
      <footer className="bg-white dark:bg-slate-900 border-t border-slate-200 dark:border-slate-800 mt-auto transition-colors duration-300">
        <div className="container-custom py-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <p className="text-slate-500 dark:text-slate-400 text-sm">
              © 2024 API Marketplace Platform. Todos os direitos reservados.
            </p>
            <div className="flex gap-6">
              <Link
                to="/support"
                className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 text-sm transition-colors"
              >
                Suporte
              </Link>
              <Link
                to="/applications"
                className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 text-sm transition-colors"
              >
                Documentação
              </Link>
              <Link
                to="/support"
                className="text-slate-500 dark:text-slate-400 hover:text-slate-800 dark:hover:text-slate-200 text-sm transition-colors"
              >
                Termos
              </Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};
